import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Loader2, ClipboardCheck, CheckCircle2 } from 'lucide-react';

const RESULT_STYLE = {
  correcto: 'bg-emerald-100 text-emerald-700',
  fuera_de_tiempo: 'bg-amber-100 text-amber-700',
  dosis_incorrecta: 'bg-red-100 text-red-700',
  omitido: 'bg-red-100 text-red-700',
  no_indicado: 'bg-purple-100 text-purple-700',
  no_realizado_opcional: 'bg-slate-100 text-slate-500',
};
const RESULT_LABEL = {
  correcto: 'Realizado correctamente', fuera_de_tiempo: 'Fuera de tiempo',
  dosis_incorrecta: 'Dosis incorrecta', omitido: 'Acción omitida',
  no_indicado: 'Acción no indicada', no_realizado_opcional: 'Opcional no realizada',
};

export const ProtocolEvaluationPanel = ({ sessionId, isInstructor }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [adjust, setAdjust] = useState({});

  const load = useCallback(() => {
    api.get(`/protocol-import/evaluate-session/${sessionId}`)
      .then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [sessionId]);
  useEffect(() => { load(); }, [load]);

  if (loading || !data || (!data.tiene_matriz && !data.evaluacion)) return null;
  const ev = data.evaluacion;

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post(`/protocol-import/evaluate-session/${sessionId}`);
      toast.success('Evaluación por protocolo generada');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al evaluar'); }
    finally { setGenerating(false); }
  };

  const saveReview = async (aprobar) => {
    const ajustes = Object.entries(adjust).map(([criterio_id, a]) => ({ criterio_id, ...a }));
    try {
      await api.put(`/protocol-import/evaluate-session/${sessionId}/review`, { ajustes, aprobar });
      toast.success(aprobar ? 'Evaluación aprobada' : 'Ajustes guardados');
      setAdjust({});
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar revisión'); }
  };

  return (
    <Card data-testid="protocol-evaluation-panel">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4 text-cyan-600" /> Evaluación por protocolo
          </CardTitle>
          <CardDescription className="text-xs">
            Comparación entre el protocolo esperado y la cronología real de la simulación
            {ev?.protocolo_origen && ` · Fuente: ${ev.protocolo_origen.filename} (v${ev.protocolo_origen.version})`}
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          {ev?.puntaje_maximo > 0 && (
            <Badge className="bg-cyan-100 text-cyan-700">{ev.puntaje_total}/{ev.puntaje_maximo} pts</Badge>
          )}
          {ev?.estado_revision === 'aprobada' && <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Aprobada</Badge>}
          {isInstructor && (
            <Button size="sm" variant="outline" onClick={generate} disabled={generating} data-testid="generate-protocol-eval-btn">
              {generating && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              {ev ? 'Regenerar' : 'Generar evaluación'}
            </Button>
          )}
        </div>
      </CardHeader>
      {ev && (
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="protocol-eval-table">
              <thead>
                <tr className="text-left text-slate-500 border-b">
                  <th className="py-1 pr-2">Código</th><th className="py-1 pr-2">Acción esperada</th>
                  <th className="py-1 pr-2">Tiempo</th><th className="py-1 pr-2">Realizado</th>
                  <th className="py-1 pr-2">Resultado</th><th className="py-1 pr-2">Puntaje</th>
                  {isInstructor && <th className="py-1">Ajuste instructor</th>}
                </tr>
              </thead>
              <tbody>
                {ev.resultados.map(r => (
                  <tr key={r.criterio_id} className="border-b last:border-0 align-top">
                    <td className="py-1.5 pr-2 font-mono">{r.codigo}</td>
                    <td className="py-1.5 pr-2">{r.accion_esperada}
                      {r.retroalimentacion && <p className="text-[10px] text-slate-400">{r.retroalimentacion}</p>}
                    </td>
                    <td className="py-1.5 pr-2">{r.detalle?.tiempo_seg != null ? `${r.detalle.tiempo_seg}s` : '—'}</td>
                    <td className="py-1.5 pr-2">{r.detalle?.realizado || '—'}
                      {r.detalle?.dosis_esperada != null && (
                        <p className="text-[10px] text-red-600">Esperado {r.detalle.dosis_esperada} · Realizado {r.detalle.dosis_realizada}</p>
                      )}
                    </td>
                    <td className="py-1.5 pr-2">
                      <Badge className={`text-[10px] ${RESULT_STYLE[r.resultado] || 'bg-slate-100'}`}>{RESULT_LABEL[r.resultado] || r.resultado}</Badge>
                      {r.ajuste_instructor && <p className="text-[10px] text-purple-600 mt-0.5">Ajustado por {r.ajuste_instructor.instructor}</p>}
                    </td>
                    <td className="py-1.5 pr-2">{r.puntaje_maximo ? `${r.puntaje_obtenido}/${r.puntaje_maximo}` : <span className="text-amber-600">pendiente</span>}</td>
                    {isInstructor && (
                      <td className="py-1.5">
                        <div className="flex gap-1">
                          <Input type="number" placeholder="pts" className="h-6 w-14 text-[10px]"
                            value={adjust[r.criterio_id]?.puntaje_obtenido ?? ''}
                            onChange={e => setAdjust(p => ({ ...p, [r.criterio_id]: { ...p[r.criterio_id], puntaje_obtenido: e.target.value ? parseInt(e.target.value) : null } }))} />
                          <Input placeholder="comentario" className="h-6 w-28 text-[10px]"
                            value={adjust[r.criterio_id]?.comentario ?? ''}
                            onChange={e => setAdjust(p => ({ ...p, [r.criterio_id]: { ...p[r.criterio_id], comentario: e.target.value } }))} />
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {isInstructor && (
            <div className="flex justify-end gap-2 pt-3">
              <Button size="sm" variant="outline" onClick={() => saveReview(false)} data-testid="save-review-btn">Guardar ajustes</Button>
              <Button size="sm" onClick={() => saveReview(true)} data-testid="approve-eval-btn">Aprobar evaluación</Button>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default ProtocolEvaluationPanel;
